import { reportPriceWithCurrency } from "../services/commerce/pricing";
import business from "../../../../config/public-business.json";
export interface TermsNotice {
  title: string;
  lastUpdated: string;
  intro: string[];
  sections: TermsNoticeSection[];
  contactEmail: string;
}

export interface TermsNoticeSection {
  title: string;
  blocks: TermsNoticeBlock[];
}

export type TermsNoticeBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "bullets"; items: string[] };

export const TERMS_NOTICE: TermsNotice = {
  title: "MoveInReport Terms, Privacy, AI, and Recording Notice",
  lastUpdated: business.policyUpdated,
  intro: [
    ...(business.legalSellerName ? [`MoveInReport is operated by ${business.legalSellerName}.`] : []),
    "MoveInReport helps renters create a user-reviewed move-in condition report. It is designed to help you organize photos and notes into a downloadable PDF.",
    "This notice explains what the app does, what it does not do, how local drafts work, how audio transcription works, and how optional analytics may be used to improve the app.",
    "By using MoveInReport, you agree to this notice. Optional analytics is separate: public users should only share optional analytics if they choose to allow it."
  ],
  sections: [
    {
      title: "Refunds and Account Removal",
      blocks: [
        { kind: "paragraph", text: "For website refunds, contact the support email below with your receipt or saved-report link. Request Apple or Google refunds through the store's purchase history. Applicable consumer rights and the payment provider's refund process apply. A refund or payment dispute can remove access to the associated cloud report." },
        { kind: "paragraph", text: "Use /account/delete to delete your account and cloud reports. Cloud cleanup normally begins within minutes; in-flight uploads are checked again after eight days. Provider recovery copies can remain for up to 40 days after cleanup starts. Outages or unresolved payments can delay cleanup. Financial providers may retain transaction records under their legal obligations." },
        { kind: "paragraph", text: "You can remove an unused unpaid cloud backup from Saved reports. Save your own PDF and editable backup before deleting important content. We do not promise permanent cloud storage or a specific period of future service." }
      ]
    },
    {
      title: "Purchases, Report Backup, and Email",
      blocks: [
        { kind: "paragraph", text: `Where purchasing is enabled, one report costs ${reportPriceWithCurrency} on the web. The app store shows the localized price for native purchases before you confirm. Web payments are processed by Stripe; Apple and Google purchases use their store billing systems, with RevenueCat validating purchase events. MoveInReport does not store card details.` },
        { kind: "paragraph", text: "Purchased PDF versions are saved privately in your account. Downloading or emailing another saved version of the same report does not require another report purchase. Sign in with the account used for the purchase to recover your PDF on another device." },
        { kind: "paragraph", text: "SendGrid delivers report emails to your verified email address. Small PDFs may be attached; larger PDFs use a link to your account. Every report email includes an account recovery link. Email delivery can fail or be delayed, so a confirmed cloud copy remains available to download independently of email." },
        { kind: "paragraph", text: "Capture works offline. Payment confirmation, online backup, transcription, and email require a connection. Keep a downloaded copy of important reports. Cloud backup is not a promise of indefinite service or protection against every possible loss." }
      ]
    },
    {
      title: "What MoveInReport Does",
      blocks: [
        {
          kind: "paragraph",
          text: "MoveInReport lets you add rooms, take photos, record or type notes, review cleaned text, and generate a PDF report."
        },
        {
          kind: "paragraph",
          text: "MoveInReport is a documentation tool only. It is not a law firm, lawyer, property inspector, contractor, insurance company, mediator, court, government agency, landlord, or property manager."
        },
        {
          kind: "paragraph",
          text: "MoveInReport does not guarantee that your landlord, property manager, court, mediator, housing agency, or anyone else will accept your report. MoveInReport does not guarantee return of a security deposit or any other outcome."
        }
      ]
    },
    {
      title: "Not Legal Advice",
      blocks: [
        { kind: "paragraph", text: "MoveInReport does not provide legal advice." },
        {
          kind: "paragraph",
          text: "Any report created with MoveInReport is created by you. You are responsible for deciding what to document, what to write, what to include, what to remove, when to send it, and who to send it to."
        },
        {
          kind: "paragraph",
          text: "If you need legal advice about your lease, security deposit, landlord, property condition, eviction, habitability, repairs, or any dispute, contact a qualified attorney or local tenant-rights organization."
        }
      ]
    },
    {
      title: "Local Drafts and Photos",
      blocks: [
        { kind: "paragraph", text: "Your draft, photos, and recordings are saved locally on the device you are using. You can capture multiple recordings for each photo and continue across reports without an internet connection." },
        {
          kind: "paragraph",
          text: "Photos are compressed and stored locally on your device. When you explicitly choose report backup and email, the finished PDF, including its photos and notes, is uploaded to private cloud storage. Photos are never sent to AI."
        },
        {
          kind: "paragraph",
          text: "A local draft is not a cloud backup. Save an editable .mir backup outside the app to preserve the photos, recordings, and notes. Restoring that file creates a separate draft; it does not restore a purchase. Only a PDF shown as successfully saved in your account has been backed up online. If you clear browser data, switch devices, lose your device, use private browsing, uninstall the browser, or your browser removes stored data, your draft may be deleted and MoveInReport may not be able to recover it."
        },
        {
          kind: "paragraph",
          text: "Browser storage is tied to the exact website address you use. A draft saved on one website address may not appear on another website address."
        },
        { kind: "paragraph", text: "Download your PDF when finished." }
      ]
    },
    {
      title: "Audio and Transcription",
      blocks: [
        {
          kind: "paragraph",
          text: "If you record a voice note and choose transcription, audio may be sent to our transcription provider only to create transcribed text from your spoken note."
        },
        {
          kind: "paragraph",
          text: "MoveInReport does not use audio to identify you, create a voiceprint, verify your identity, or determine who is speaking."
        },
        {
          kind: "paragraph",
          text: "MoveInReport is designed not to intentionally store your audio on our servers after transcription. Each recording stays on this device until you remove it, mark its condition note ready, or delete its draft. Saved transcripts remain associated with their photos. Transcription resumes while the app is open and online."
        },
        {
          kind: "paragraph",
          text: "Do not include private or sensitive information in audio unless you decide it is necessary for your own documentation."
        }
      ]
    },
    {
      title: "AI Use",
      blocks: [
        { kind: "paragraph", text: "AI is used only to transcribe and lightly clean your spoken or typed note." },
        {
          kind: "paragraph",
          text: "AI may remove filler words, repeated starts, coughs, and obvious speech artifacts. AI may fix grammar, punctuation, and capitalization."
        },
        {
          kind: "paragraph",
          text: "AI does not inspect photos. AI does not identify damage. AI does not determine cause. AI does not assign responsibility. AI does not decide whether something is normal wear and tear. AI does not decide whether something is legally important. AI does not provide legal advice."
        },
        {
          kind: "paragraph",
          text: "You must review and approve all text before export. Do not rely on AI-generated text without checking it yourself."
        }
      ]
    },
    {
      title: "Your Responsibility for the Report",
      blocks: [
        { kind: "paragraph", text: "You are responsible for the content of your report." },
        {
          kind: "paragraph",
          text: "Before downloading or sharing a report, you should review every photo, title, description, timestamp, and any optional property information you enter."
        },
        {
          kind: "paragraph",
          text: "You should remove or edit anything that is inaccurate, unclear, private, unnecessary, or sensitive."
        },
        {
          kind: "paragraph",
          text: "You should not include people, IDs, financial documents, medical information, passwords, account numbers, private communications, or other sensitive information in photos or notes unless you decide it is necessary for your own documentation."
        }
      ]
    },
    {
      title: "Recording Warning",
      blocks: [
        { kind: "paragraph", text: "Record only your own narration." },
        {
          kind: "paragraph",
          text: "Do not record conversations with landlords, property managers, roommates, contractors, guests, neighbors, or any other person unless everyone involved has given permission."
        },
        {
          kind: "paragraph",
          text: "Recording laws vary by location. In some places, recording a private or confidential conversation without everyone's consent may be illegal."
        },
        { kind: "paragraph", text: "You are responsible for following the recording laws that apply to you." }
      ]
    },
    {
      title: "Acceptable Use",
      blocks: [
        { kind: "paragraph", text: "You agree not to use MoveInReport to:" },
        {
          kind: "bullets",
          items: [
            "break the law",
            "violate anyone's privacy",
            "secretly record other people",
            "harass, threaten, defame, or impersonate anyone",
            "upload or create illegal, abusive, or harmful content",
            "interfere with the security or operation of the app",
            "attempt to reverse engineer, overload, scrape, attack, or misuse the service",
            "use the service in a way that creates unreasonable cost, risk, or disruption"
          ]
        },
        { kind: "paragraph", text: "MoveInReport may limit, block, or disable access if the service is being abused." }
      ]
    },
    {
      title: "Optional Product Analytics and App Diagnostics",
      blocks: [
        {
          kind: "paragraph",
          text: "MoveInReport may ask whether you want to share optional, privacy-preserving app analytics. These analytics help us understand whether the app works, where people get stuck, which features are useful, and whether errors are happening."
        },
        {
          kind: "paragraph",
          text: "If you allow optional analytics, MoveInReport may collect limited app usage and diagnostic information such as app opens, screens viewed, workflow steps, report generated/downloaded events, room count, condition-note count, ready/incomplete counts, approximate file sizes, PDF generation time, photo compression timing, transcription success or failure, safe error codes, coarse browser/platform category, viewport class, app version, and anonymous session or install identifiers."
        },
        {
          kind: "paragraph",
          text: "Optional analytics does not include your photos, thumbnails, raw audio, typed notes, transcripts, AI cleaned text, property address, room names, filenames, screenshots, PDFs, or report contents."
        },
        {
          kind: "paragraph",
          text: "You can use MoveInReport without optional analytics. If you turn optional analytics off or do not allow it, core app features should still work."
        },
        {
          kind: "paragraph",
          text: "Some limited technical logs may still be processed when needed to provide the service, protect the app from abuse, troubleshoot server errors, process transcription requests, or maintain security."
        },
        {
          kind: "paragraph",
          text: "Raw optional analytics events are intended to be kept for up to 90 days. Aggregated metrics may be kept longer. Admin access audit logs may be kept for up to one year."
        }
      ]
    },
    {
      title: "Third-Party Services",
      blocks: [
        {
          kind: "paragraph",
          text: "MoveInReport uses Google Cloud and Firebase for hosting, authentication, private PDF backups, and service records; OpenAI for transcription; Stripe for web purchases; Apple or Google and RevenueCat for store purchases; and SendGrid for report email."
        },
        {
          kind: "paragraph",
          text: "These services may process limited technical information needed to provide and protect the service, such as requests, device/browser information, network information, audio sent for transcription, safe error logs, optional analytics events if allowed, admin authentication information, or payment information if you choose to support the project."
        },
        { kind: "paragraph", text: "PDF generation runs on your device. If you choose online backup, its photos and notes are uploaded as part of the finished PDF. Photos are never sent to AI." },
        {
          kind: "paragraph",
          text: "MoveInReport does not send your report text, photos, room names, PDF, condition-note details, or transcript text to analytics services or payment providers."
        },
        {
          kind: "paragraph",
          text: "If you choose to support the project, payment information is handled by the payment provider and not by MoveInReport."
        }
      ]
    },
    {
      title: "Information MoveInReport Does Not Sell or Use for Ads",
      blocks: [
        { kind: "paragraph", text: "MoveInReport does not sell your photos, audio, notes, reports, or personal information." },
        {
          kind: "paragraph",
          text: "MoveInReport does not share your photos, notes, reports, or personal information for cross-context behavioral advertising."
        },
        {
          kind: "paragraph",
          text: "MoveInReport does not use your photos, notes, audio, transcripts, or reports for advertising."
        },
        { kind: "paragraph", text: "MoveInReport does not use your photos to train AI models." },
        { kind: "paragraph", text: "MoveInReport does not use third-party advertising trackers in the MVP app." }
      ]
    },
    {
      title: "Your Privacy Choices",
      blocks: [
        { kind: "paragraph", text: "You can choose not to use voice transcription by typing notes instead." },
        {
          kind: "paragraph",
          text: "You can choose not to allow optional analytics if the app asks for analytics permission."
        },
        { kind: "paragraph", text: "You can delete your local draft inside MoveInReport if the delete option is available." },
        {
          kind: "paragraph",
          text: "You can also delete local app data by clearing your browser data for moveinreport.com, or for the exact older website address you used if your draft was created there."
        },
        {
          kind: "paragraph",
          text: "Because drafts are stored locally on your device, MoveInReport may not have access to your draft and may not be able to delete or recover it for you."
        }
      ]
    },
    {
      title: "Security",
      blocks: [
        {
          kind: "paragraph",
          text: "MoveInReport is designed to minimize data collection by keeping drafts local, uploading a PDF only when you choose backup, sending audio only with transcription consent, and collecting limited optional analytics if you allow it."
        },
        {
          kind: "paragraph",
          text: "No website, browser, device, network, or third-party service can be guaranteed to be perfectly secure."
        },
        { kind: "paragraph", text: "Avoid including unnecessary sensitive information in photos, notes, or audio." }
      ]
    },
    {
      title: "Age Requirement",
      blocks: [
        { kind: "paragraph", text: "MoveInReport is intended for adults." },
        {
          kind: "paragraph",
          text: "You must be at least 18 years old, or the age of majority where you live, to use MoveInReport."
        },
        {
          kind: "paragraph",
          text: "MoveInReport is not intended for children under 13 and should not be used by children under 13."
        }
      ]
    },
    {
      title: "No Professional Inspection",
      blocks: [
        { kind: "paragraph", text: "MoveInReport is not a property inspection service." },
        {
          kind: "paragraph",
          text: "MoveInReport does not verify property condition, detect hidden defects, identify hazards, estimate repair costs, or determine whether a condition violates a lease, law, code, or regulation."
        },
        {
          kind: "paragraph",
          text: "If you need a professional inspection, safety evaluation, repair assessment, code review, or legal opinion, contact a qualified professional."
        }
      ]
    },
    {
      title: "No Warranty",
      blocks: [
        { kind: "paragraph", text: "MoveInReport is provided as is and as available." },
        {
          kind: "paragraph",
          text: "MoveInReport may contain errors, interruptions, delays, transcription mistakes, export failures, browser-storage failures, or other limitations."
        },
        {
          kind: "paragraph",
          text: "MoveInReport makes no warranties that the app will be uninterrupted, error-free, accepted as evidence, legally sufficient, or suitable for your specific situation."
        }
      ]
    },
    {
      title: "Limitation of Liability",
      blocks: [
        {
          kind: "paragraph",
          text: "To the maximum extent allowed by law, MoveInReport and its operator are not liable for lost deposits, lost rent, legal disputes, rejected reports, inaccurate notes, transcription errors, missing photos, lost drafts, deleted browser data, device failures, service interruptions, third-party service failures, or any indirect, incidental, special, consequential, or punitive damages."
        },
        { kind: "paragraph", text: "Your use of MoveInReport is your choice and your responsibility." }
      ]
    },
    {
      title: "Changes to This Notice",
      blocks: [
        { kind: "paragraph", text: "MoveInReport may update this notice from time to time." },
        { kind: "paragraph", text: "The updated version will be posted in the app or on moveinreport.com." },
        {
          kind: "paragraph",
          text: "If a future update materially changes optional analytics or privacy practices, MoveInReport should show a new in-app notice before applying the new optional analytics behavior."
        }
      ]
    },
    {
      title: "Contact",
      blocks: [{ kind: "paragraph", text: "For privacy or support questions, contact:" }]
    }
  ],
  contactEmail: business.supportEmail
};
